import React from 'react';
import { Helmet } from 'react-helmet';
import { useIntl } from '@ttstr/components/Intl/IntlContext';

import { AppState } from '@ttstr/reducers';
import { Ticket } from '@ttstr/api/products';
import { getLocationFromProduct } from '@ttstr/reducers/locations';

import { ProductDetailsContext, useProductDetails, useProductDetailLink, useShopConfig } from '@ttstr/components';
import { useShallowEqualSelector } from '@ttstr/utils';

interface Props {
  ticket: Ticket;
  dontChangeMetaDescription?: boolean;
}

/**
 * Generates Event structured data (JSON-LD) for ticket products.
 *
 * Check Google's guidelines:
 * https://developers.google.com/search/docs/advanced/structured-data/event#technical-guidelines
 */

// --- Schema.org type definitions ---

interface AddressSchema {
  '@type': 'PostalAddress';
  streetAddress?: string;
  postalCode?: string;
  addressLocality?: string;
}

interface LocationSchema {
  '@type': 'Place';
  name?: string;
  latitude?: number;
  longitude?: number;
  address: AddressSchema;
}

interface OfferSchema {
  '@type': 'Offer';
  availability: string;
  price?: number;
  priceCurrency: string;
  validFrom?: string;
  validThrough?: string;
  url: string;
}

interface PerformerSchema {
  '@type': string;
  name: string;
}

interface OrganizationSchema {
  '@type': 'Organization';
  name: string;
  url?: string;
}

interface EventSchema {
  '@context': 'https://schema.org';
  '@type': 'Event';
  name: string;
  url: string;
  description?: string;
  startDate?: string;
  endDate?: string;
  doorTime?: string;
  eventStatus: string;
  eventAttendanceMode: string;
  inLanguage?: string;
  image: string[];
  location?: LocationSchema;
  offers: OfferSchema;
  performer?: PerformerSchema | PerformerSchema[];
  organizer?: OrganizationSchema;
}

// --- Helpers ---

function stripHtml(html: string): string {
  if (!html) return '';
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent || div.innerText || '';
}

// --- Component ---

const TicketMarkup: React.FC<Props> = ({ ticket, dontChangeMetaDescription = false }) => {
  const { language } = useIntl();
  const { canonicalUrl } = useShopConfig();
  const productDetailLink = useProductDetailLink();
  const { currency } = useShallowEqualSelector(mapStateToProps);
  const { locations } = React.useContext(ProductDetailsContext);
  const location = getLocationFromProduct(locations, ticket);
  const { brands, merchants } = useProductDetails();

  const productUrl = productDetailLink(ticket, { canonicalUrl });

  const images = React.useMemo(
    () =>
      [ticket.image?.url, ...ticket.gallery_image_urls.map((i) => i.original)]
        .filter(Boolean)
        .map((url) => canonicalUrl + url),
    [ticket, canonicalUrl]
  );

  const jsonLd = React.useMemo<EventSchema>(() => {
    const schema: EventSchema = {
      '@context': 'https://schema.org',
      '@type': 'Event',
      name: ticket.title,
      url: productUrl,
      eventStatus: 'https://schema.org/EventScheduled',
      eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
      inLanguage: language,
      image: images,
      offers: {
        '@type': 'Offer',
        availability:
          ticket.sold_out_status === 'sold_out' ? 'https://schema.org/SoldOut' : 'https://schema.org/InStock',
        priceCurrency: currency ?? 'EUR',
        url: productUrl,
      },
    };

    // Description
    if (ticket.description) {
      schema.description = stripHtml(ticket.description);
    }

    // Dates
    if (ticket.valid_start_on) schema.startDate = String(ticket.valid_start_on);
    if (ticket.valid_end_on) schema.endDate = String(ticket.valid_end_on);
    if (ticket.time_open) schema.doorTime = ticket.time_open;

    // Offer price
    if (ticket.min_price != null) schema.offers.price = ticket.min_price;
    if (ticket.available_start_at) schema.offers.validFrom = String(ticket.available_start_at);
    if (ticket.available_end_at) schema.offers.validThrough = String(ticket.available_end_at);

    // Location
    if (location.title || location.street || location.city) {
      schema.location = {
        '@type': 'Place',
        address: {
          '@type': 'PostalAddress',
        },
      };
      if (location.title) schema.location.name = location.title;
      if (location.latitude) schema.location.latitude = location.latitude;
      if (location.longitude) schema.location.longitude = location.longitude;
      if (location.street) schema.location.address.streetAddress = location.street;
      if (location.city) schema.location.address.addressLocality = location.city;
      if (location.zip_code) schema.location.address.postalCode = location.zip_code;
    }

    // Performer(s)
    if (ticket.brand_ids?.length) {
      if (!Array.isArray(ticket.brand_ids)) {
        // Single brand_id (number)
        const brand = brands[(ticket.brand_ids as unknown) as number];
        if (brand) {
          schema.performer = { '@type': 'PerformingGroup', name: brand.title };
        }
      } else {
        const performers = ticket.brand_ids
          .map((id) => brands[id])
          .filter(Boolean)
          .map((brand) => ({ '@type': 'PerformingGroup', name: brand.title }));
        if (performers.length === 1) {
          schema.performer = performers[0];
        } else if (performers.length > 1) {
          schema.performer = performers;
        }
      }
    }

    // Organizer
    const merchant = ticket.merchant_id != null ? merchants[ticket.merchant_id] : undefined;
    if (merchant) {
      schema.organizer = {
        '@type': 'Organization',
        name: merchant.title,
      };
      if (merchant.website) {
        schema.organizer.url = merchant.website;
      }
    }

    return schema;
  }, [ticket, images, currency, productUrl, location, brands, merchants, language]);

  const pageTitle =
    'Tickets für ' +
    ticket.title +
    (location.city ? ' in ' + location.city : '') +
    (ticket.valid_start_on
      ? ' - ' +
        new Date(ticket.valid_start_on).toLocaleDateString(language, {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        })
      : '') +
    (location.title ? ', ' + location.title : '') +
    (location.city ? ', ' + location.city : '');

  const plainDescription = ticket.description ? stripHtml(ticket.description).substring(0, 200) : '';

  // Google requires name, startDate, and location (with address data) for Event structured data.
  // Skip the JSON-LD script entirely when any required property is missing.
  const hasRequiredStructuredData =
    Boolean(ticket.title) && Boolean(ticket.valid_start_on) && Boolean(location.street || location.city);

  React.useEffect(() => {
    if (!hasRequiredStructuredData) {
      const missing: string[] = [];
      if (!ticket.title) missing.push('name (ticket.title)');
      if (!ticket.valid_start_on) missing.push('startDate (ticket.valid_start_on)');
      if (!location.street && !location.city) missing.push('location.address (no street or city)');
      console.warn(
        `[TicketMarkup] Event structured data (JSON-LD) not rendered for ticket ${
          ticket.id
        }: missing required properties: ${missing.join(', ')}`
      );
    }
  }, [hasRequiredStructuredData, ticket.title, ticket.valid_start_on, ticket.id, location.street, location.city]);

  return (
    <Helmet>
      <title>{pageTitle}</title>

      {!dontChangeMetaDescription && plainDescription && <meta name="description" content={plainDescription} />}

      <meta property="og:title" content={pageTitle} />
      {plainDescription && <meta property="og:description" content={plainDescription} />}
      <meta property="og:url" content={productUrl} />
      {ticket.image?.url && <meta property="og:image" content={canonicalUrl + ticket.image.url} />}

      {hasRequiredStructuredData && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
      <link rel="canonical" href={productUrl} />
    </Helmet>
  );
};

const mapStateToProps = (state: AppState) => {
  const { settings } = state.Settings;
  return {
    currency: settings.config_currency,
  };
};

export default React.memo(TicketMarkup);
